import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import { apiFetch } from "../api";
import "./SessionResults.css";

const SessionResults = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [session, setSession] = useState(null);
  const [batchResults, setBatchResults] = useState([]);
  const [unprocessedNotes, setUnprocessedNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchSessionResults();
  }, [sessionId]);

  const fetchSessionResults = async () => {
    setLoading(true);
    setError("");

    try {
      // Get session results
      const response = await apiFetch(`/sessions/${sessionId}/results`);

      if (!response.ok) {
        throw new Error("Failed to fetch session results");
      }

      const data = await response.json();
      setSession({ id: data.session_id, status: data.status });
      setBatchResults(data.batches || []);
      setUnprocessedNotes(data.unprocessed_notes || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const formatValue = (value) => {
    // Handle CuppingScore / ScaAffectiveScore format: {score: number | null, notes: string}
    if (typeof value === "object" && value !== null) {
      if ("notes" in value) {
        return {
          score:
            value.score !== null && value.score !== undefined
              ? value.score
              : null,
          notes: value.notes || "",
        };
      }
      // Partial object (e.g. {score: 7} from partial edit without notes key)
      if ("score" in value && typeof value.score === "number") {
        return { score: value.score, notes: "" };
      }
      // Unknown object shape — don't crash, render nothing
      return { score: null, notes: "" };
    }
    return { score: null, notes: value != null ? String(value) : "—" };
  };

  const CuppingNotesDisplay = ({ notes }) => {
    const fields = [
      { key: "dry_aroma", label: "Dry Aroma" },
      { key: "wet_aroma", label: "Wet Aroma" },
      { key: "flavor_hot", label: "Flavor (Hot)" },
      { key: "flavor_cooling", label: "Flavor (Cooling)" },
      { key: "aftertaste", label: "Aftertaste" },
      { key: "acidity", label: "Acidity" },
      { key: "sweetness", label: "Sweetness" },
      { key: "body", label: "Body" },
      { key: "balance", label: "Balance" },
      { key: "defects", label: "Defects" },
      { key: "overall_impression", label: "Overall Impression" },
    ];

    return (
      <div className="cupping-notes">
        {fields.map((field) => {
          const value = formatValue(notes[field.key]);
          return (
            <div key={field.key} className="note-field">
              <div className="note-field-header">
                <strong>{field.label}</strong>
                <span className="note-score">
                  {value.score !== null ? value.score : ""}
                </span>
              </div>
              <div className="note-field-content">{value.notes}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const CuppingNotesEditor = ({ notes, onChange }) => {
    const fields = [
      { key: "dry_aroma", label: "Dry Aroma" },
      { key: "wet_aroma", label: "Wet Aroma" },
      { key: "flavor_hot", label: "Flavor (Hot)" },
      { key: "flavor_cooling", label: "Flavor (Cooling)" },
      { key: "aftertaste", label: "Aftertaste" },
      { key: "acidity", label: "Acidity" },
      { key: "sweetness", label: "Sweetness" },
      { key: "body", label: "Body" },
      { key: "balance", label: "Balance" },
      { key: "defects", label: "Defects" },
      { key: "overall_impression", label: "Overall Impression" },
    ];

    return (
      <div className="cupping-notes-editor">
        {fields.map((field) => {
          const fieldValue = notes[field.key] || { score: null, notes: "" };
          return (
            <div key={field.key} className="note-field-editor">
              <label>{field.label}</label>
              <div className="field-inputs">
                <input
                  type="number"
                  min="1"
                  max="5"
                  step="0.1"
                  placeholder="Score (1-5)"
                  value={fieldValue.score || ""}
                  onChange={(e) =>
                    onChange(
                      field.key,
                      "score",
                      e.target.value ? parseFloat(e.target.value) : null,
                    )
                  }
                  className="score-input"
                />
                <textarea
                  placeholder="Notes"
                  value={fieldValue.notes || ""}
                  onChange={(e) => onChange(field.key, "notes", e.target.value)}
                  className="notes-textarea"
                  rows="2"
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const ScaAffectiveDisplay = ({ notes }) => {
    const fields = [
      { key: "fragrance", label: "Fragrance" },
      { key: "aroma", label: "Aroma" },
      { key: "flavor", label: "Flavor" },
      { key: "aftertaste", label: "Aftertaste" },
      { key: "acidity", label: "Acidity" },
      { key: "sweetness", label: "Sweetness" },
      { key: "mouthfeel", label: "Mouthfeel" },
      { key: "overall", label: "Overall" },
    ];

    return (
      <div className="cupping-notes sca-affective">
        {fields.map((field) => {
          const value = formatValue(notes[field.key]);
          return (
            <div key={field.key} className="note-field">
              <div className="note-field-header">
                <strong>{field.label}</strong>
                <span className="note-score">
                  {value.score !== null ? value.score : ""}
                </span>
              </div>
              <div className="note-field-content">{value.notes}</div>
            </div>
          );
        })}
        <div className="note-field cup-counts">
          <div className="note-field-header">
            <strong>Non-uniform cups:</strong>
            <span className="note-score">{notes.non_uniform_cups ?? 0}</span>
          </div>
          <div className="note-field-header">
            <strong>Defective cups:</strong>
            <span className="note-score">{notes.defective_cups ?? 0}</span>
          </div>
        </div>
      </div>
    );
  };

  const ScaAffectiveEditor = ({ notes, onChange }) => {
    const fields = [
      { key: "fragrance", label: "Fragrance" },
      { key: "aroma", label: "Aroma" },
      { key: "flavor", label: "Flavor" },
      { key: "aftertaste", label: "Aftertaste" },
      { key: "acidity", label: "Acidity" },
      { key: "sweetness", label: "Sweetness" },
      { key: "mouthfeel", label: "Mouthfeel" },
      { key: "overall", label: "Overall" },
    ];

    return (
      <div className="cupping-notes-editor sca-affective-editor">
        {fields.map((field) => {
          const fieldValue = notes[field.key] || { score: null, notes: "" };
          return (
            <div key={field.key} className="note-field-editor">
              <label>{field.label}</label>
              <div className="field-inputs">
                <input
                  type="number"
                  min="1"
                  max="9"
                  step="1"
                  placeholder="Score (1-9)"
                  value={fieldValue.score || ""}
                  onChange={(e) =>
                    onChange(
                      field.key,
                      "score",
                      e.target.value ? parseInt(e.target.value, 10) : null,
                    )
                  }
                  className="score-input"
                />
                <textarea
                  placeholder="Notes"
                  value={fieldValue.notes || ""}
                  onChange={(e) => onChange(field.key, "notes", e.target.value)}
                  className="notes-textarea"
                  rows="2"
                />
              </div>
            </div>
          );
        })}
        <div className="note-field-editor cup-counts-editor">
          <label>Non-uniform cups (0-5)</label>
          <input
            type="number"
            min="0"
            max="5"
            step="1"
            value={notes.non_uniform_cups ?? 0}
            onChange={(e) => {
              const val = e.target.value ? parseInt(e.target.value, 10) : 0;
              onChange(
                "non_uniform_cups",
                "value",
                Math.max(0, Math.min(5, val)),
              );
            }}
            className="cup-count-input"
          />
          <label>Defective cups (0-5)</label>
          <input
            type="number"
            min="0"
            max="5"
            step="1"
            value={notes.defective_cups ?? 0}
            onChange={(e) => {
              const val = e.target.value ? parseInt(e.target.value, 10) : 0;
              onChange(
                "defective_cups",
                "value",
                Math.max(0, Math.min(5, val)),
              );
            }}
            className="cup-count-input"
          />
        </div>
      </div>
    );
  };

  const BatchCard = ({ batch }) => {
    const [expanded, setExpanded] = useState(true);
    const [activeProtocolIndex, setActiveProtocolIndex] = useState(0);
    const [isEditing, setIsEditing] = useState(false);
    const [editedNotes, setEditedNotes] = useState({});
    const [saving, setSaving] = useState(false);

    const protocolNotes = batch.protocol_notes || [];
    const activeProtocol = protocolNotes[activeProtocolIndex] || {};
    const protocolName = activeProtocol.protocol_name;
    const isSca = protocolName === "SCA Affective Assessment";
    const hasMultipleProtocols = protocolNotes.length > 1;

    const handleEdit = () => {
      setEditedNotes(activeProtocol.cupping_notes || {});
      setIsEditing(true);
    };

    const handleCancel = () => {
      setEditedNotes(activeProtocol.cupping_notes || {});
      setIsEditing(false);
    };

    const handleFieldChange = (fieldKey, property, value) => {
      setEditedNotes((prev) => {
        if (property === "value") {
          return { ...prev, [fieldKey]: value };
        }
        return {
          ...prev,
          [fieldKey]: {
            ...prev[fieldKey],
            [property]: value,
          },
        };
      });
    };

    const handleSave = async () => {
      setSaving(true);
      try {
        const response = await apiFetch(
          `/sessions/${sessionId}/batches/${activeProtocol.batch_notes_id}`,
          {
            method: "PATCH",
            body: JSON.stringify(editedNotes),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to update cupping notes");
        }

        const data = await response.json();

        // Update the correct protocol entry in state
        setBatchResults((prevBatches) =>
          prevBatches.map((b) => {
            if (b.id !== batch.id) return b;
            return {
              ...b,
              protocol_notes: b.protocol_notes.map((pn, i) =>
                i === activeProtocolIndex
                  ? {
                      ...pn,
                      cupping_notes: data.cupping_notes,
                      display_score: data.cupping_notes?.display_score,
                    }
                  : pn,
              ),
            };
          }),
        );

        setIsEditing(false);
      } catch (err) {
        setError(err.message);
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="batch-card">
        <div className="batch-header" onClick={() => setExpanded(!expanded)}>
          <h3>
            {batch.roaster_name
              ? `${batch.roaster_name} - ${batch.batch_label}`
              : batch.batch_label}
          </h3>
          <span className="expand-icon">{expanded ? "−" : "+"}</span>
        </div>

        {expanded && (
          <div className="batch-content">
            {/* Protocol Tabs */}
            {hasMultipleProtocols && (
              <div className="protocol-tabs">
                {protocolNotes.map((pn, i) => (
                  <button
                    key={i}
                    className={`protocol-tab ${i === activeProtocolIndex ? "active" : ""}`}
                    onClick={() => {
                      setActiveProtocolIndex(i);
                      setIsEditing(false);
                    }}
                  >
                    {pn.protocol_name}
                    {pn.display_score != null && (
                      <span className="tab-score"> ({pn.display_score})</span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Display Score */}
            {activeProtocol.display_score != null && !isEditing && (
              <div className="display-score">
                Score: <strong>{activeProtocol.display_score}</strong>
              </div>
            )}

            {isEditing ? (
              <>
                {isSca ? (
                  <ScaAffectiveEditor
                    notes={editedNotes}
                    onChange={handleFieldChange}
                  />
                ) : (
                  <CuppingNotesEditor
                    notes={editedNotes}
                    onChange={handleFieldChange}
                  />
                )}
                <div className="edit-actions">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="btn-save"
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="btn-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                {isSca ? (
                  <ScaAffectiveDisplay
                    notes={activeProtocol.cupping_notes || {}}
                  />
                ) : (
                  <CuppingNotesDisplay
                    notes={activeProtocol.cupping_notes || {}}
                  />
                )}
                <div className="edit-actions">
                  <button onClick={handleEdit} className="btn-edit">
                    Edit Notes
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">Loading results...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate("/sessions")} className="btn-back">
          ← Back to Sessions
        </button>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Header user={user} onLogout={handleLogout} />

      <main className="dashboard-content session-results">
        <div className="results-header">
          <h2>Session</h2>
          {session && (
            <span className="status-badge status-complete">
              {session.status}
            </span>
          )}
        </div>

        {batchResults.length === 0 ? (
          <div className="no-results">
            <p>No batches were processed. Please review your session.</p>
          </div>
        ) : (
          <>
            <div className="batches-section">
              <h3>Batches Processed ({batchResults.length})</h3>
              {batchResults.map((batch) => (
                <BatchCard key={batch.id} batch={batch} />
              ))}
            </div>

            {unprocessedNotes.length > 0 && (
              <div className="unprocessed-notes">
                <h3>Unprocessed Notes ({unprocessedNotes.length})</h3>
                {unprocessedNotes.map((note, index) => (
                  <div key={index} className="unprocessed-note">
                    <p>{note}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className="results-actions">
          <button onClick={() => navigate("/sessions")} className="btn-back">
            ← Back to Sessions
          </button>
        </div>
      </main>
    </div>
  );
};

export default SessionResults;
